import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import {
  cambiarAreasUsuario,
  cambiarCelularUsuario,
  cambiarNombreUsuario,
  cambiarOrganizacionesUsuario,
  cambiarRolesUsuario,
  crearUsuario,
  type CrearUsuarioInput,
} from "../services/usuarios-service";

export function useCrearUsuario(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearUsuarioInput) => crearUsuario(input),
    onSuccess: (_data, input) => {
      toastExito({ title: "Usuario creado", message: input.email });
      void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useCambiarNombre() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, fullName }: { usuarioId: string; fullName: string | null }) =>
      cambiarNombreUsuario(usuarioId, fullName),
    onSuccess: () => {
      toastExito({ title: "Nombre actualizado", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
    },
    onError: toastErrorApi,
  });
}

export function useCambiarCelular() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, celular }: { usuarioId: string; celular: string | null }) =>
      cambiarCelularUsuario(usuarioId, celular),
    onSuccess: () => {
      toastExito({ title: "Celular actualizado", message: "Se usará para SMS y WhatsApp." });
      void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
    },
    onError: toastErrorApi,
  });
}

/**
 * Las tres mutations comparten estrategia: PUT con set completo +
 * invalidación del listado. Cada una tiene su propio toast porque el
 * usuario quiere saber qué guardó.
 */
export function useCambiarRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, roles }: { usuarioId: string; roles: string[] }) =>
      cambiarRolesUsuario(usuarioId, roles),
    onSuccess: () => {
      toastExito({ title: "Roles actualizados", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
    },
    onError: toastErrorApi,
  });
}

export function useCambiarAreas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ usuarioId, areaIds }: { usuarioId: string; areaIds: string[] }) =>
      cambiarAreasUsuario(usuarioId, areaIds),
    onSuccess: () => {
      toastExito({ title: "Áreas actualizadas", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
    },
    onError: toastErrorApi,
  });
}

export function useCambiarOrganizaciones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      usuarioId,
      organizacionIds,
    }: {
      usuarioId: string;
      organizacionIds: string[];
    }) => cambiarOrganizacionesUsuario(usuarioId, organizacionIds),
    onSuccess: () => {
      toastExito({ title: "Organizaciones actualizadas", message: "Cambios guardados." });
      void qc.invalidateQueries({ queryKey: qk.adminUsuarios });
    },
    onError: toastErrorApi,
  });
}
